import { useEffect, useState } from 'react';

/**
 * Reads the design tokens out of CSS and hands them to code that cannot use
 * `var(...)`.
 *
 * ECharts writes concrete colour values onto the elements it draws, so it needs
 * hex, not custom properties. Rather than keep a second copy of the palette in
 * TypeScript — which would drift from tokens.css the first time someone edits
 * one and not the other — we resolve the live computed values at render time.
 * tokens.css stays the single source of truth.
 */

export interface Theme {
  mode: 'light' | 'dark';
  plane: string;
  surface: string;
  surfaceSunken: string;
  ink: string;
  inkSecondary: string;
  inkMuted: string;
  border: string;
  grid: string;
  axis: string;
  brand: string;
  /** Categorical slots, in fixed order. Never cycle past the end — fold to "Other". */
  series: string[];
  /** Single-hue magnitude ramp, light end first. */
  sequential: string[];
  ordinal: string[];
  statusGood: string;
  statusWarning: string;
  statusSerious: string;
  statusCritical: string;
  deltaUp: string;
  deltaDown: string;
  font: string;
  shadowPop: string;
}

const SERIES_COUNT = 8;

/**
 * Chart forms where any two marks can land side by side — scatter, bubble,
 * choropleth, small multiples — only clear the all-pairs colourblind test on
 * the first three slots. Past three, fold the tail into "Other" or facet.
 */
export const ALL_PAIRS_SERIES_CAP = 3;

const readVar = (styles: CSSStyleDeclaration, name: string): string =>
  styles.getPropertyValue(name).trim();

export const readTheme = (): Theme => {
  const styles = getComputedStyle(document.documentElement);
  const get = (name: string) => readVar(styles, name);

  // `color-scheme` is what tokens.css actually switches, so it is the honest
  // answer to "which set of values did the cascade pick?" — more reliable than
  // re-deriving the media query and the data-theme attribute here.
  const scheme = get('color-scheme');
  const mode: Theme['mode'] = scheme.includes('dark') ? 'dark' : 'light';

  return {
    mode,
    plane: get('--plane'),
    surface: get('--surface'),
    surfaceSunken: get('--surface-sunken'),
    ink: get('--ink'),
    inkSecondary: get('--ink-secondary'),
    inkMuted: get('--ink-muted'),
    border: get('--border'),
    grid: get('--grid'),
    axis: get('--axis'),
    brand: get('--brand'),
    series: Array.from({ length: SERIES_COUNT }, (_, i) => get(`--series-${i + 1}`)),
    sequential: [100, 200, 300, 400, 500, 600, 700].map((step) => get(`--seq-${step}`)),
    ordinal: [1, 2, 3, 4, 5].map((step) => get(`--ord-${step}`)),
    statusGood: get('--status-good'),
    statusWarning: get('--status-warning'),
    statusSerious: get('--status-serious'),
    statusCritical: get('--status-critical'),
    deltaUp: get('--delta-up'),
    deltaDown: get('--delta-down'),
    font: get('--font'),
    shadowPop: get('--shadow-pop'),
  };
};

/**
 * The resolved theme, kept current as the platform switches light/dark.
 *
 * Two triggers, because the platform can drive either one: the iframe inherits
 * `color-scheme` (so `prefers-color-scheme` flips), and an in-app toggle would
 * stamp `data-theme` on <html>.
 */
export const useTheme = (): Theme => {
  const [theme, setTheme] = useState<Theme>(readTheme);

  useEffect(() => {
    const update = () => setTheme(readTheme());

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', update);

    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'style', 'class'],
    });

    return () => {
      media.removeEventListener('change', update);
      observer.disconnect();
    };
  }, []);

  return theme;
};

/**
 * Series colour for slot `index`.
 *
 * Assignment is by position and never wraps: colour follows the entity, so a
 * filter that drops series 2 must not repaint series 3. Anything past the last
 * slot gets muted ink — the caller should have folded it into "Other" before
 * getting here, and a grey mark makes that oversight visible rather than
 * inventing a ninth hue that was never validated.
 */
export const seriesColor = (theme: Theme, index: number): string =>
  theme.series[index] ?? theme.inkMuted;
